import React, { useState, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';

import createStyle from 'shared-utils/src/createStyle';

import ModalContainer from '../ModalContainer';
import ModalHeader from '../ModalHeader';
import ModalBody from '../ModalBody';

import { chooseTransformClass } from './helpers';

import styles, { mainCls } from './style';

const useStyles = createStyle(styles);

const MultiPanelModal = forwardRef(({
  panels,
  components,
  getPanelProps,
  setFooter,
  getModalTitle,
  hidePanelContents,
  getPrevpanelState,
  setBodyOnScroll,
  resetActions,
  modalData,
  version,
  backEventAdditionalConf = {},
  closeEventAdditionalConf = {},
}, ref) => {
  const [currentPanel, setCurrentPanel] = useState(panels.father);
  const [currentPanelAction, setCurrentPanelAction] = useState(null);
  const [loaded, setLoaded] = useState([panels.father.name, panels.father.nextPanel]);

  const _goToPrevPanel = ({ prevPanel }) => {
    if (prevPanel === 'father' && currentPanelAction) {
      setCurrentPanelAction(null);
    }    
    setCurrentPanel(panels[prevPanel]);
  };

  const _createBackButton = ({ prevPanel, panelCustomStyle }) => (panelCustomStyle ? {
    action: () => {
      modalData.restoreInitialSize();
      hidePanelContents({ panel: currentPanel });
      setTimeout(() => {
        if (getPrevpanelState) {
          getPrevpanelState({ prevPanel });
        }
        _goToPrevPanel({ prevPanel });
      }, 300);
    }
  } : {
    action: () => {
      if (getPrevpanelState) {
        getPrevpanelState({ prevPanel });
      }
      _goToPrevPanel({ prevPanel });
    },
  });

  const changeCurrentPanelActions = ({ choosenPanel, choosenAction }) => {    
    if (!panels[choosenPanel].previousPanel) {
      panels[choosenPanel].previousPanel = currentPanel.name;
    }
    if (choosenAction) {
      setCurrentPanelAction(choosenAction);
    }
    setCurrentPanel(panels[choosenPanel]);
  };

  const _changeCurrentPanel = ({ choosenPanel, choosenAction }) => {
    const realChoosenPanel = panels[choosenPanel] ? choosenPanel : 'father'; 
    if (!loaded.includes(realChoosenPanel)) {
      setLoaded([...loaded, realChoosenPanel]);
    }

    if (currentPanel.config.customStyle) {
      modalData.restoreInitialSize();
      hidePanelContents({ panel: currentPanel });
      setTimeout(() => changeCurrentPanelActions({ choosenPanel: realChoosenPanel, choosenAction }), 300);
    } else {
      changeCurrentPanelActions({ choosenPanel: realChoosenPanel, choosenAction });
    }
  };

  useImperativeHandle(ref, () => ({
    getCurrentPanelName() {
      return currentPanel.name;
    },
    goToPrevPanel({ prevPanel }) {
      _goToPrevPanel({ prevPanel });
    },
    createBackButton({ prevPanel }) {
      _createBackButton({ prevPanel });
    },
    changeCurrentPanel({ choosenPanel, choosenAction }) {
      _changeCurrentPanel({ choosenPanel, choosenAction });
    }
  }));

  const classes = useStyles({ panels, version });
  const mainClsStr = classes[mainCls];

  return (
    <ModalContainer>
      {
        Object.keys(panels).map((keyPanel, index) => {
          const panel = panels[keyPanel];          
          const transformClass = chooseTransformClass({ currentIndex: currentPanel?.index, indexPanel: index });
          const headerPanel = panel?.config?.header;
          const panelCustomStyle = panel?.config?.customStyle || null;
          const hasKeyBackButton = 'backButton' in headerPanel
          const hasKeyCloseButton = 'closeButton' in headerPanel
          const hasBackButton = (keyPanel !== 'father' && !hasKeyBackButton) || (hasKeyBackButton && headerPanel.backButton)
          const hasCloseButton = (keyPanel !== 'father' && !hasKeyCloseButton) || (hasKeyCloseButton && headerPanel.closeButton)
          const backButtonAction = panel.config.header.resetButton
            ? { action: resetActions[keyPanel] }
            : _createBackButton({ prevPanel: panel.previousPanel, panelCustomStyle });
          const modalBodyOnScroll = () => setBodyOnScroll && setBodyOnScroll({ panel });

          const isActivePanel = currentPanel?.index === index;

          return (
            <div key={index} className={`${mainClsStr}__panel${transformClass ? ` ${mainClsStr}__panel--${transformClass}` : ''} is-anim`}>
              <ModalHeader
                version={panel.version}
                compact
                title={getModalTitle({ panel, variant: currentPanelAction })}
                data={modalData}
                backButton={hasBackButton ? backButtonAction : null}
                backButtonAndClose={hasBackButton && hasCloseButton}
                closeButton={hasCloseButton}
                customCloseAction={panel.config.trackOnClose && modalData.close}
                trackOnClose={panel.config.trackOnClose && {
                  ...panel.config.trackOnClose,
                  ...{
                    defaultDimension: {
                      ...panel.config.trackOnClose.defaultDimension,
                      ...closeEventAdditionalConf,
                    },
                  }
                }}
                trackOnBack={panel.config.trackOnBack && {
                  ...panel.config.trackOnBack,
                  ...{
                    defaultDimension: {
                      ...panel.config.trackOnBack.defaultDimension,
                      ...backEventAdditionalConf,
                    },
                  }
                }}
              />
              <ModalBody
                version={panel.version}
                full={modalData.fullContent}
                onScroll={modalBodyOnScroll}
                className="is-rel"
              >
                {loaded.includes(keyPanel) ? components[panel.config.shape]({ ...getPanelProps({ panel }), isActivePanel, customStyle: panelCustomStyle, currentPanelAction }) : null}
              </ModalBody>
              {setFooter({ panel })}
            </div>
          )
        })
      }
    </ModalContainer>
  );
});

const propTypes = {
  panels: PropTypes.instanceOf(Object).isRequired,
  components: PropTypes.instanceOf(Object).isRequired,
  getPanelProps: PropTypes.func,
  setFooter: PropTypes.func,
  getModalTitle: PropTypes.func,
  setBodyOnScroll: PropTypes.func,
  getPrevpanelState: PropTypes.func,
  modalData: PropTypes.instanceOf(Object).isRequired,
};
const defaultProps = {
  getPanelProps: () => ({}),
  setFooter: null,
  getModalTitle: () => {},
  setBodyOnScroll: null,
  getPrevpanelState: null,
};

MultiPanelModal.defaultProps = defaultProps;
MultiPanelModal.propTypes = propTypes;

export default MultiPanelModal;